import { Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { DataPersistence } from '@nrwl/angular';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { OemRooftopLinkQueryParameters } from '@quorum/models/xs-query';
import {
  Colour,
  DeclinedReason,
  DirectMessageTemplate,
  Employee,
  MessageType,
  ProvinceState,
  ServiceCode,
  SubType,
  TechnicianGroup,
  TransportOption,
  VehicleInspectionWearPattern,
  VehicleMake,
  WarrantySubmissionType,
} from '@quorum/models/xs-resource';
import { of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import {
  GetAssociateClassesFromServer,
  GetAssociateClassesFromServerSuccess,
  GetDeclinedReasonsFromServer,
  GetDeclinedReasonsFromServerSuccess,
  GetDirectMessageTemplatesFromServer,
  GetDirectMessageTemplatesFromServerSuccess,
  GetEmployeesFromServer,
  GetEmployeesFromServerSuccess,
  GetLocationsFromServer,
  GetLocationsFromServerSuccess,
  GetMessageTypesFromServer,
  GetMessageTypesFromServerSuccess,
  GetProvinceStatesFromServer,
  GetProvinceStatesFromServerSuccess,
  GetServiceCodesFromServer,
  GetServiceCodesFromServerSuccess,
  GetTechnicianGroupsFromServer,
  GetTechnicianGroupsFromServerSuccess,
  GetTransportOptionsFromServer,
  GetTransportOptionsFromServerSuccess,
  GetVehicleColoursFromServer,
  GetVehicleColoursFromServerSuccess,
  GetVehicleInspectionWearPatternsFromServer,
  GetVehicleInspectionWearPatternsFromServerSuccess,
  GetVehicleMakesFromServer,
  GetVehicleMakesFromServerSuccess,
  GetVehicleSubTypesFromServer,
  GetVehicleSubTypesFromServerSuccess,
  GetWarrantySubmissionTypesFromServer,
  GetWarrantySubmissionTypesFromServerSuccess,
  ServiceLaneDropdownsActionTypes,
} from './service-lane-dropdowns.actions';
import { DeclinedReasonWithDefault, ServiceLaneDropdownsPartialState } from './service-lane-dropdowns.reducer';

@Injectable()
export class ServiceLaneDropdownsEffects {
  getLocationsFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetLocationsFromServer, {
      run: (action: GetLocationsFromServer, state: any) => {
        return this.apiService
          .get<MessageType[]>(`v/1/associates/locations`, {
            params: action.payload,
          })
          .pipe(
            map((locations) => {
              return new GetLocationsFromServerSuccess(locations);
            })
          );
      },
      onError: (action: GetLocationsFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getMessageTypesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetMessageTypesFromServer, {
      run: (action: GetMessageTypesFromServer, state: any) => {
        return this.apiService
          .get<MessageType[]>(`v/1/associates/message-types`, {
            params: action.payload,
          })
          .pipe(
            map((messageTypes) => {
              return new GetMessageTypesFromServerSuccess(messageTypes);
            })
          );
      },
      onError: (action: GetMessageTypesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getAssociateClassesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetAssociateClassesFromServer, {
      run: (action: GetAssociateClassesFromServer, state: any) => {
        return this.apiService
          .get<MessageType[]>(`v/1/associates/associate-classes`, {
            params: action.payload,
          })
          .pipe(
            map((classes) => {
              return new GetAssociateClassesFromServerSuccess(classes);
            })
          );
      },
      onError: (action: GetAssociateClassesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getProvinceStatesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetProvinceStatesFromServer, {
      run: (action: GetProvinceStatesFromServer, state: any) => {
        return this.apiService
          .get<ProvinceState[]>(`v/1/associates/province-states`, {
            params: action.payload,
          })
          .pipe(
            map((provinceStates) => {
              return new GetProvinceStatesFromServerSuccess(provinceStates);
            })
          );
      },
      onError: (action: GetProvinceStatesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getEmployeesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetEmployeesFromServer, {
      run: (action: GetEmployeesFromServer, state: any) => {
        return this.apiService
          .get<Employee[]>(`v/1/associates/employees`, {
            params: action.payload,
          })
          .pipe(
            map((Employees) => {
              return new GetEmployeesFromServerSuccess(Employees);
            })
          );
      },
      onError: (action: GetEmployeesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getServiceCodesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetServiceCodesFromServer, {
      run: (action: GetServiceCodesFromServer, state: any) => {
        return this.apiService
          .get<ServiceCode[]>(`v/1/fixed-operations/service-codes`, {
            params: action.payload,
          })
          .pipe(
            map((servicecodes) => {
              return new GetServiceCodesFromServerSuccess(servicecodes);
            })
          );
      },
      onError: (action: GetServiceCodesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getTechnicianGroupsFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetTechnicianGroupsFromServer, {
      run: (action: GetTechnicianGroupsFromServer, state: any) => {
        return this.apiService
          .get<TechnicianGroup[]>(`v/1/associates/technician-groups`, {
            params: action.payload,
          })
          .pipe(
            map((techGroups) => {
              return new GetTechnicianGroupsFromServerSuccess(techGroups);
            })
          );
      },
      onError: (action: GetTechnicianGroupsFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getVehicleColoursFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetVehicleColoursFromServer, {
      run: (action: GetVehicleColoursFromServer, state: any) => {
        return this.apiService
          .get<Colour[]>(`v/1/vehicles/vehicle-colours`, {
            params: action.payload,
          })
          .pipe(
            map((vehicleColours) => {
              return new GetVehicleColoursFromServerSuccess(vehicleColours);
            })
          );
      },
      onError: (action: GetVehicleColoursFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getVehicleSubTypesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetVehicleSubTypesFromServer, {
      run: (action: GetVehicleSubTypesFromServer, state: any) => {
        return this.apiService
          .get<SubType[]>(`v/1/vehicles/vehicle-sub-types`, {
            params: action.payload,
          })
          .pipe(
            map((vehicleSubTypes) => {
              return new GetVehicleSubTypesFromServerSuccess(vehicleSubTypes);
            })
          );
      },
      onError: (action: GetVehicleSubTypesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getVehicleMakesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetVehicleMakesFromServer, {
      run: (action: GetVehicleMakesFromServer, state: any) => {
        return this.apiService
          .get<VehicleMake[]>(`v/1/vehicles/vehicle-makes`, {
            params: action.payload,
          })
          .pipe(
            map((vehicleMakes) => {
              return new GetVehicleMakesFromServerSuccess(vehicleMakes);
            })
          );
      },
      onError: (action: GetVehicleMakesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getWarrantySubmissionTypesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetWarrantySubmissionTypesFromServer, {
      run: (action: GetWarrantySubmissionTypesFromServer, state: any) => {
        return this.apiService
          .get<WarrantySubmissionType[]>(`v/1/fixed-operations/warranty-submission-types`, {
            params: action.payload,
          })
          .pipe(
            map((subTypes) => {
              return new GetWarrantySubmissionTypesFromServerSuccess(subTypes);
            })
          );
      },
      onError: (action: GetWarrantySubmissionTypesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getDirectMessageTemplatesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetDirectMessageTemplatesFromServer, {
      run: (action: GetDirectMessageTemplatesFromServer, state: any) => {
        return this.apiService
          .get<DirectMessageTemplate[]>(`v/1/communicator/message-templates/direct`, {
            params: action.payload,
          })
          .pipe(
            map((directMessageTemplates) => {
              return new GetDirectMessageTemplatesFromServerSuccess(directMessageTemplates);
            })
          );
      },
      onError: (action: GetDirectMessageTemplatesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getTransportOptionsFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetTransportOptionsFromServer, {
      run: (action: GetTransportOptionsFromServer, state: any) => {
        return this.apiService
          .get<TransportOption[]>(`v/2/fixed-operations/transport-options`, {
            params: action.payload,
          })
          .pipe(
            map((transportOptions) => {
              return new GetTransportOptionsFromServerSuccess(transportOptions);
            })
          );
      },
      onError: (action: GetTransportOptionsFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getDeclinedReasonsFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetDeclinedReasonsFromServer, {
      run: (action: GetDeclinedReasonsFromServer, state: any) => {
        return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
          filter((employee) => employee != null),
          take(1),
          switchMap((employee) => {
            return this.apiService.get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`).pipe(
              switchMap((oemRooftopLink) => {
                if (oemRooftopLink) {
                  switch (oemRooftopLink.associateId) {
                    case 'TOY':
                      return of(
                        new GetDeclinedReasonsFromServerSuccess([
                          { id: 1, reason: 'Will Complete At Next Visit', isDefault: false },
                          { id: 2, reason: 'Customer Declined', isDefault: false },
                          { id: 3, reason: 'Performed Elsewhere', isDefault: false },
                          { id: 4, reason: 'Service Not Required', isDefault: false },
                          { id: 5, reason: 'No Time To Perform Campaign Today', isDefault: false },
                          { id: 6, reason: 'Other', isDefault: true },
                        ])
                      );
                    default:
                      return this.apiService
                        .get<DeclinedReason[]>(`v/1/fixed-operations/declined-reasons`, {
                          params: action.payload,
                        })
                        .pipe(
                          map((declinedReasons) => {
                            const declinedReasonsWithDefault: DeclinedReasonWithDefault[] = [];
                            declinedReasons.forEach((declinedReason: DeclinedReason) => {
                              if (declinedReason.reason.toLowerCase() === 'no reason') {
                                declinedReasonsWithDefault.push({
                                  ...declinedReason,
                                  isDefault: true,
                                });
                              } else {
                                declinedReasonsWithDefault.push({
                                  ...declinedReason,
                                  isDefault: false,
                                });
                              }
                            });
                            return new GetDeclinedReasonsFromServerSuccess(declinedReasonsWithDefault);
                          })
                        );
                      break;
                  }
                }
                return null;
              })
            );
          })
        );
      },
      onError: (action: GetDeclinedReasonsFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getVehicleInspectionWearPatternsFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneDropdownsActionTypes.GetVehicleInspectionWearPatternsFromServer, {
      run: (action: GetVehicleInspectionWearPatternsFromServer, state: any) => {
        return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
          filter((employee) => employee !== null),
          take(1),
          switchMap((employee) => {
            const payload = {
              ...action.payload,
              rooftopId: employee.rooftopId.toString(),
            };
            return this.apiService.get<VehicleInspectionWearPattern[]>(
              `v/1/vehicles/vehicle-inspection-wear-patterns`,
              { params: payload }
            );
          }),
          map((vehicleInspectionWearPatterns) => {
            return new GetVehicleInspectionWearPatternsFromServerSuccess(vehicleInspectionWearPatterns);
          })
        );
      },
      onError: (action: GetVehicleInspectionWearPatternsFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  constructor(
    private dataPersistence: DataPersistence<ServiceLaneDropdownsPartialState>,
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService
  ) {}
}
